import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/layout';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { 
  Users, Mail, TrendingUp, Award, Edit3, Send, DollarSign, 
  FileText, AlertCircle, CheckCircle, Clock, BarChart3 
} from 'lucide-react';

const CouncillorDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [profile, setProfile] = useState(null);
  const [credits, setCredits] = useState(null);
  const [emailBlasts, setEmailBlasts] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchDashboardData();
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      const [dashRes, profileRes, creditsRes, blastsRes] = await Promise.all([
        api.get('/councillors/dashboard'),
        api.get('/councillors/my-profile'),
        api.get('/councillors/email-credits'),
        api.get('/councillors/email-blasts'),
      ]);

      setDashboardData(dashRes.data);
      setProfile(profileRes.data);
      setCredits(creditsRes.data);
      setEmailBlasts(blastsRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (error.response?.status === 403) {
        toast.error('You must be a verified councillor to access this page');
        navigate('/');
      } else {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <div className="text-white font-mono">LOADING DASHBOARD...</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!dashboardData) {
    return (
      <Layout>
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="text-red-500 font-mono">Failed to load dashboard</div>
        </div>
      </Layout>
    );
  }

  const councillor = dashboardData.councillor;
  const stats = dashboardData;

  return (
    <Layout>
      <div className="min-h-screen bg-black text-white">
        {/* Header */}
        <div className="border-b border-gray-800 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold font-mono">
                  COUNCILLOR <span className="text-green-500">DASHBOARD</span>
                </h1>
                <p className="text-gray-400 mt-1">
                  {councillor.name} • {councillor.ward}
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/councillor/profile/edit')}
                  className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 px-4 py-2 rounded transition-colors"
                >
                  <Edit3 className="w-4 h-4" />
                  Edit Profile
                </button>
                <button
                  onClick={() => navigate('/councillor/email-blast/new')}
                  className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-black font-bold px-4 py-2 rounded transition-colors"
                >
                  <Send className="w-4 h-4" />
                  New Email
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-6 mt-6 border-b border-gray-800">
              {[
                { id: 'overview', label: 'Overview', icon: BarChart3 },
                { id: 'emails', label: 'Email Blasts', icon: Mail },
                { id: 'profile', label: 'Profile', icon: FileText },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 pb-3 border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-green-500 text-white'
                      : 'border-transparent text-gray-400 hover:text-white'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 py-8">
          {activeTab === 'overview' && (
            <div>
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {/* Total Posts */}
                <div className="bg-gray-900 border border-gray-800 rounded-sm p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-mono uppercase text-gray-400">Ward Posts</div>
                    <FileText className="w-5 h-5 text-blue-500" />
                  </div>
                  <div className="text-3xl font-bold">{stats.posts.total}</div>
                  <div className="text-sm text-gray-500 mt-1">
                    {stats.posts.issues} issues • {stats.posts.positive} positive
                  </div>
                </div>

                {/* Messages */}
                <div className="bg-gray-900 border border-gray-800 rounded-sm p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-mono uppercase text-gray-400">Messages</div>
                    <Mail className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div className="text-3xl font-bold">{stats.messages.total}</div>
                  <div className="text-sm text-gray-500 mt-1">
                    {stats.messages.pending} pending • {stats.messages.replied} replied
                  </div>
                </div>

                {/* Responsiveness */}
                <div className="bg-gray-900 border border-gray-800 rounded-sm p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-mono uppercase text-gray-400">Response Rate</div>
                    <TrendingUp className="w-5 h-5 text-green-500" />
                  </div>
                  <div className={`text-3xl font-bold ${
                    stats.messages.responsiveness_rate >= 70 ? 'text-green-500' :
                    stats.messages.responsiveness_rate >= 40 ? 'text-yellow-500' : 'text-red-500'
                  }`}>
                    {stats.messages.responsiveness_rate}%
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {stats.messages.responsiveness_rate >= 70 ? 'Excellent' :
                     stats.messages.responsiveness_rate >= 40 ? 'Good' : 'Needs Improvement'}
                  </div>
                </div>

                {/* Subscribers */}
                <div className="bg-gray-900 border border-gray-800 rounded-sm p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-mono uppercase text-gray-400">Subscribers</div>
                    <Users className="w-5 h-5 text-purple-500" />
                  </div>
                  <div className="text-3xl font-bold">{stats.subscribers}</div>
                  <div className="text-sm text-gray-500 mt-1">Ward email list</div>
                </div>
              </div>

              {/* Email Credits Card */}
              {credits && (
                <div className="bg-gradient-to-br from-green-900/20 to-black border-2 border-green-500/30 rounded-sm p-6 mb-8">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <DollarSign className="w-6 h-6 text-green-500" />
                        <h3 className="text-xl font-bold">Email Credits</h3>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-gray-400">Free Credits Remaining:</span>
                          <span className="text-2xl font-bold text-green-500">{credits.free_remaining}</span>
                          <span className="text-sm text-gray-500">of {credits.free_credits_total}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-gray-400">Paid Credits:</span>
                          <span className="text-2xl font-bold">{credits.paid_credits}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-gray-400">Premium Tracking:</span>
                          <span className={`text-sm font-bold ${credits.has_premium_tracking ? 'text-green-500' : 'text-gray-500'}`}>
                            {credits.has_premium_tracking ? 'Enabled ✓' : 'Not Enabled'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-sm text-gray-400 mb-4">Need more credits?</p>
                      <button
                        onClick={() => navigate('/councillor/purchase-credits')}
                        className="bg-green-500 hover:bg-green-600 text-black font-bold px-6 py-3 rounded transition-colors"
                      >
                        Purchase Credits
                      </button>
                      <div className="mt-2 text-xs text-gray-500">
                        $100 single • $250 for 3
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Ward Link */}
              <div className="bg-gray-900 border border-gray-800 rounded-sm p-6">
                <h3 className="text-lg font-bold mb-4">Your Public Ward Page</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Share this link with your constituents to help them stay connected
                </p>
                <div className="flex gap-3">
                  <input
                    type="text"
                    readOnly
                    value={`${window.location.origin}/ward/ward-${councillor.ward.replace('Ward ', '')}`}
                    className="flex-1 bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white font-mono text-sm"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/ward/ward-${councillor.ward.replace('Ward ', '')}`);
                      toast.success('Link copied!');
                    }}
                    className="bg-gray-800 hover:bg-gray-700 border border-gray-700 px-4 py-2 rounded transition-colors"
                  >
                    Copy
                  </button>
                  <a
                    href={`/ward/ward-${councillor.ward.replace('Ward ', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-green-500 hover:bg-green-600 text-black font-bold px-4 py-2 rounded transition-colors"
                  >
                    View
                  </a>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'emails' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Email Blasts</h2>
                <button
                  onClick={() => navigate('/councillor/email-blast/new')}
                  className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-black font-bold px-4 py-2 rounded transition-colors"
                >
                  <Send className="w-4 h-4" />
                  Create New
                </button>
              </div>

              {emailBlasts.length === 0 ? (
                <div className="bg-gray-900 border border-gray-800 rounded-sm p-12 text-center">
                  <Mail className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                  <p className="text-gray-400 mb-4">You haven't created any email blasts yet</p>
                  <button
                    onClick={() => navigate('/councillor/email-blast/new')}
                    className="bg-green-500 hover:bg-green-600 text-black font-bold px-6 py-3 rounded transition-colors"
                  >
                    Create Your First Email
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {emailBlasts.map((blast) => (
                    <div
                      key={blast.id}
                      className="bg-gray-900 border border-gray-800 rounded-sm p-6 hover:border-gray-700 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-lg font-bold mb-1">{blast.subject}</h3>
                          <p className="text-sm text-gray-400">
                            Created: {new Date(blast.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div>
                          {blast.status === 'draft' && (
                            <span className="inline-flex items-center gap-1 bg-gray-800 text-gray-300 px-3 py-1 rounded text-sm">
                              <Edit3 className="w-3 h-3" />
                              Draft
                            </span>
                          )}
                          {blast.status === 'pending_review' && (
                            <span className="inline-flex items-center gap-1 bg-yellow-900/30 text-yellow-500 px-3 py-1 rounded text-sm">
                              <Clock className="w-3 h-3" />
                              Pending Review
                            </span>
                          )}
                          {blast.status === 'approved' && (
                            <span className="inline-flex items-center gap-1 bg-green-900/30 text-green-500 px-3 py-1 rounded text-sm">
                              <CheckCircle className="w-3 h-3" />
                              Approved
                            </span>
                          )}
                          {blast.status === 'rejected' && (
                            <span className="inline-flex items-center gap-1 bg-red-900/30 text-red-500 px-3 py-1 rounded text-sm">
                              <AlertCircle className="w-3 h-3" />
                              Rejected
                            </span>
                          )}
                          {blast.status === 'sent' && (
                            <span className="inline-flex items-center gap-1 bg-blue-900/30 text-blue-500 px-3 py-1 rounded text-sm">
                              <CheckCircle className="w-3 h-3" />
                              Sent
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <p className="text-gray-300 text-sm mb-4 line-clamp-2">
                        {blast.subject ? `Subject: ${blast.subject}` : '(No subject)'}
                      </p>
                      
                      {blast.status === 'draft' && (
                        <div className="flex gap-3">
                          <button
                            onClick={() => navigate(`/councillor/email-blast/${blast.id}/edit`)}
                            className="text-sm bg-gray-800 hover:bg-gray-700 border border-gray-700 px-4 py-2 rounded transition-colors"
                          >
                            Edit
                          </button>
                        </div>
                      )}
                      
                      {blast.admin_review_notes && blast.status === 'rejected' && (
                        <div className="mt-4 bg-red-900/10 border border-red-500/30 rounded p-3">
                          <p className="text-sm text-red-400">
                            <strong>Admin Note:</strong> {blast.admin_review_notes}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'profile' && profile && (
            <div>
              <h2 className="text-2xl font-bold mb-6">Profile Information</h2>
              
              <div className="bg-gray-900 border border-gray-800 rounded-sm p-6 mb-6">
                <div className="flex flex-col md:flex-row gap-8">
                  <div className="flex-shrink-0">
                    <img
                      src={councillor.image_url}
                      alt={councillor.name}
                      className="w-32 h-32 rounded object-cover border-2 border-gray-700"
                      onError={(e) => {
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(councillor.name)}&background=1a1a1a&color=fff&size=128`;
                      }}
                    />
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-2">{councillor.name}</h3>
                    <p className="text-green-500 font-mono mb-4">{councillor.ward}</p>
                    <p className="text-gray-300 mb-4">{councillor.bio}</p>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex gap-2">
                        <span className="text-gray-400">Email:</span>
                        <span className="text-white">{councillor.email}</span>
                      </div>
                      {councillor.phone && (
                        <div className="flex gap-2">
                          <span className="text-gray-400">Phone:</span>
                          <span className="text-white">{councillor.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate('/councillor/profile/edit')}
                className="bg-green-500 hover:bg-green-600 text-black font-bold px-6 py-3 rounded transition-colors"
              >
                Edit Profile
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CouncillorDashboard;
